import type { FC } from 'react';
import { Box, Typography } from '@mui/material';
import { makeStyles } from '@mui/styles';
import type { Theme } from '@mui/material';
import { useTranslation } from 'react-i18next';

import type { AccountingType } from 'v2-types/order';
import {
  type DebtEquityContext,
  type GroupedMetrics,
  type RevenueEntry,
  type RevenueKPIs,
} from '../types';
import KPIMetricCard from './KPIMetricCard';
import MetricGroup from './MetricGroup';
import RevenueDetailTable from './RevenueDetailTable';
import RevenueReconciliation from './RevenueReconciliation';

const useStyles = makeStyles((theme: Theme) => ({
  page: {
    minWidth: 0,
    minHeight: '100%',
    padding: theme.spacing(3),
    backgroundColor: theme.palette.background.default,
    [theme.breakpoints.down('sm')]: {
      padding: theme.spacing(2),
    },
  },
  header: {
    minWidth: 0,
    marginBottom: theme.spacing(3),
    paddingBottom: theme.spacing(2),
    borderBottom: `1px solid ${theme.palette.divider}`,
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
      paddingBottom: theme.spacing(1.5),
    },
  },
  title: {
    color: theme.palette.primary.main,
    fontWeight: 600,
    overflowWrap: 'anywhere' as const,
  },

  sourceCount: {
    display: 'block',
    marginTop: theme.spacing(0.5),
    color: '#000000',
  },
  kpiSection: {
    marginBottom: theme.spacing(3),
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(2),
    },
  },
  sectionTitle: {
    marginBottom: theme.spacing(1.5),
    color: '#000000',
    fontWeight: 500,
    [theme.breakpoints.down('sm')]: {
      marginBottom: theme.spacing(1),
    },
  },
  kpiGrid: {
    display: 'grid',
    gap: theme.spacing(3),
    gridTemplateColumns: '1fr',
    [theme.breakpoints.down('sm')]: {
      gap: theme.spacing(1.5),
    },
    [theme.breakpoints.up('sm')]: {
      gridTemplateColumns: '1fr 1fr',
    },
    [theme.breakpoints.up('lg')]: {
      gridTemplateColumns: 'repeat(5, minmax(0, 1fr))',
    },
  },
}));

type RevenueViewProps = {
  readonly grouped: GroupedMetrics;
  readonly kpis: RevenueKPIs;
  readonly debtEquityContext: DebtEquityContext;
  readonly revenueEntries: readonly AccountingType[];
};

type MetricFootnote = {
  readonly text: string;
  readonly color: 'error' | 'textSecondary';
};

type GroupDefinition = {
  readonly id: string;
  readonly title: string;
  readonly subtitle: string;
  readonly entries: readonly RevenueEntry[];
  readonly footnoteMap?: Partial<Record<string, MetricFootnote>>;
};

const RevenueView: FC<RevenueViewProps> = ({
  grouped,
  kpis,
  debtEquityContext,
  revenueEntries,
}) => {
  const classes = useStyles();
  const { t } = useTranslation('revenue');

  const labelOverrides: Partial<Record<string, string>> = {
    revenue_invoiced: t('metric.revenueInvoiced', 'Revenue Invoiced (accrued)'),
    revenue_collected: t('metric.revenueCollected', 'Revenue Collected (cash)'),
  };

  const freeCashFlowNote = t(
    'kpi.freeCashFlowContext',
    'No separate capex entry is included in this revenue model; FCF is shown as supplied.'
  );

  const executiveFootnotes: Partial<Record<string, MetricFootnote>> = {
    cashflow_free: {
      text: freeCashFlowNote,
      color: 'textSecondary',
    },
    conversion_cash: {
      text: t('metric.cashConversionContext', 'Cash conversion is shown as a percentage from the model.'),
      color: 'textSecondary',
    },
  };

  if (debtEquityContext.isNegativeEquity) {
    executiveFootnotes.debt_equity = {
      text: debtEquityContext.equityContext,
      color: 'error',
    };
  }

  const groups: GroupDefinition[] = [
    {
      id: 'revenue-executive-metrics',
      title: t('group.executive.title', 'Executive metrics'),
      subtitle: t(
        'group.executive.subtitle',
        'Yield, liquidity, balance-sheet position, and operating efficiency.'
      ),
      entries: grouped.executive,
      footnoteMap: executiveFootnotes,
    },
    {
      id: 'revenue-derived-metrics',
      title: t('group.derived.title', 'Derived metrics'),
      subtitle: t(
        'group.derived.subtitle',
        'Profitability, collection, return, and working-capital indicators supplied by accounting.'
      ),
      entries: grouped.derived,
    },
    {
      id: 'revenue-base-metrics',
      title: t('group.base.title', 'Base metrics'),
      subtitle: t(
        'group.base.subtitle',
        'Recorded revenue and cost amounts. Invoiced is accrual; collected is cash.'
      ),
      entries: grouped.base,
    },
  ];

  const kpiItems = [
    {
      code: 'profit_net',
      label: t('kpi.netProfit', 'Net Profit'),
      entry: kpis.netProfit,
    },
    {
      code: 'return_investment',
      label: t('kpi.roi', 'Return on Investment'),
      entry: kpis.roi,
    },
    {
      code: 'rate_collection',
      label: t('kpi.collectionRate', 'Revenue Collection Rate'),
      entry: kpis.collectionRate,
    },
    {
      code: 'cashflow_free',
      label: t('kpi.freeCashFlow', 'Free Cash Flow'),
      entry: kpis.freeCashFlow,
      footnote: freeCashFlowNote,
    },
  ].sort((a, b) =>
    a.label.localeCompare(b.label, undefined, { sensitivity: 'base' })
  );

  const allEntries = groups.reduce<RevenueEntry[]>(
    (entries, group) => [...entries, ...group.entries],
    []
  );

  return (
    <Box className={classes.page}>
      <Box component="header" className={classes.header}>
        <Typography component="h1" variant="h5" className={classes.title}>
          {t('title', 'Revenue')}
        </Typography>
        <Typography variant="caption" className={classes.sourceCount}>
          {t('sourceCount', '{{count}} revenue entries returned by accounting', {
            count: revenueEntries.length,
          })}
        </Typography>
      </Box>

      <Box component="section" className={classes.kpiSection} aria-labelledby="revenue-priority-heading">
        <Typography
          component="h2"
          variant="h6"
          id="revenue-priority-heading"
          className={classes.sectionTitle}
        >
          {t('priorityMetrics', 'Priority metrics')}
        </Typography>
        <Box className={classes.kpiGrid}>
          {kpiItems.map((item) => (
            <div key={item.code}>
              <KPIMetricCard
                entry={item.entry}
                labelOverride={item.label}
                footnote={item.footnote}
              />
            </div>
          ))}
        </Box>
      </Box>

      {groups.map((group) => (
        <MetricGroup
          key={group.id}
          sectionId={group.id}
          title={group.title}
          subtitle={group.subtitle}
          entries={group.entries}
          labelOverrides={labelOverrides}
          footnoteMap={group.footnoteMap}
        />
      ))}

      <RevenueReconciliation grouped={grouped} />
      <RevenueDetailTable entries={allEntries} />
    </Box>
  );
};

export { RevenueView };
export default RevenueView;
